import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Snackbar,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    FormControl,
    FormGroup,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    Checkbox,
    Autocomplete,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
      QuestionAnswer as VerifyIcon
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import FileUploadComponent from '../../../components/FileUploadComponent';
import LaboratoryService from '../../../services/laboratoryService';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import {
    insertDANC,
    updateDANC,
    updateDANCBySourceId,
    getDANCById,
    getDANC
} from '../../../services/apiCalibration';
import {
    getScrutinyRemarks,
    createScrutinyRemark,
    getClauseNo
} from '../../../services/api';
import s3Utils from '../../../utils/s3Utils';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import useAssessmentTracking from "../../../hooks/useAssessmentTracking";
import {
  LocationWarningDialog,
  HourlyAttendanceDialog,
} from "../../../components/AssessmentTrackingDialogs";

// Adequacy Check Component with DA NC Integration on Submit
const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0,
    getAssessmentIdFromQuery,
    getAssessorIdFromQuery,
    refreshDANCData // Add refresh callback prop
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    
    // State for scrutiny remarks
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [isDropdownSelection, setIsDropdownSelection] = useState(false);

    // Clause number states
    const [clauses, setClauses] = useState([]);
    const [loadingClauses, setLoadingClauses] = useState(false);

    // Multiple clause selection states for checkbox functionality
    const [selectedClauses, setSelectedClauses] = useState([]);
    const [clauseSearchFilter, setClauseSearchFilter] = useState('');
    const clauseSearchInputRef = React.useRef(null);

    // Load existing DA NC data on component mount
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!orgId || !appId || !title || dataLoaded) return;

            try {
                setDancLoading(true);
                
                // Determine NCtype based on userTypeId
                const userTypeId = localStorage.getItem('userTypeId');
                let expectedNCType = 'Document Adequacy'; // Default
                
                if (userTypeId === '4') {
                    expectedNCType = 'Document Adequacy';
                } else if (userTypeId === '6') {
                    expectedNCType = 'Assessment';
                }
                
                console.log(`🔍 Loading DANC data for userType ${userTypeId} with NCtype: ${expectedNCType}`);
                
                // Use getDANC with filters to get records for this section
                const response = await getDANC({
                    orgid: orgId,
                    appid: appId,
                    sectionName: title,
                    subsectionName: sectionId,
                    isactive: 1
                });
                
                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    console.log(`📋 Found ${response.data.data.length} total DANC records for section: ${title}`);
                    
                    // Filter by NCtype to get the record for current userType
                    const filteredRecords = response.data.data.filter(record => 
                        record.NCtype === expectedNCType
                    );
                    
                    console.log(`🎯 Filtered to ${filteredRecords.length} records with NCtype: ${expectedNCType}`);
                    
                    if (filteredRecords.length > 0) {
                        const recordData = filteredRecords[0];
                        console.log(`✅ Existing Record found with NCtype ${recordData.NCtype}:`, recordData);
                        setExistingDANCRecord(recordData);
                        
                        // Bind DA NC data to adequacy check fields
                        console.log(`🔗 Binding DANC data to adequacy check section: ${sectionId}`);
                        
                        if (recordData.InformationStatus) {
                            console.log(`📊 Setting adequacy check status: ${recordData.InformationStatus}`);
                            handleAdequacyCheck(sectionId, recordData.InformationStatus);
                        }
                        
                        // Use both InformationProvided and Observation fields for remarks
                        const remarks = recordData.InformationProvided || recordData.Observation || '';
                        if (remarks) {
                            console.log(`📝 Setting adequacy check remarks: ${remarks}`);
                            handleAdequacyRemarks(sectionId, remarks);
                        }
                    } else {
                        console.log(`ℹ️ No existing DANC record found with NCtype: ${expectedNCType} for section: ${title}`);
                        setExistingDANCRecord(null);
                    }
                } else {
                    setExistingDANCRecord(null);
                }
                setDataLoaded(true);
            } catch (error) {
                console.log('No existing DA NC record found or error loading:', error.message);
                setDancError(`Error loading DA-NC data: ${error.message}`);
                setDataLoaded(true);
            } finally {
                setDancLoading(false);
            }
        };

        loadExistingDANCData();
    }, [orgId, appId, title, sectionId, dataLoaded, handleAdequacyCheck, handleAdequacyRemarks]);

    // Update existingDANCRecord when adequacyChecks change (after refresh)
    useEffect(() => {
        const adequacy = adequacyChecks[sectionId];
        if (adequacy && adequacy.dancRecord) {
            console.log(`🔄 Updating existingDANCRecord from adequacyChecks:`, adequacy.dancRecord);
            setExistingDANCRecord(adequacy.dancRecord);
        }
    }, [adequacyChecks, sectionId]);
    const location = useLocation();
     const getCapacityFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedCapacity = searchParams.get("capacity");
        if (encryptedCapacity) {
          try {
            const decryptedCapacity = decryptText(encryptedCapacity);
            console.log(
              "📍 Using decrypted capacity from URL query:",
              decryptedCapacity
            );
            return decryptedCapacity;
          } catch (error) {
            console.warn("Error decrypting capacity:", error);
            return encryptedCapacity;
          }
        }
        return null;
      }, [location.search]);
    

    // Load clause numbers for Organization section
    useEffect(() => {
         const loadClauseNumbers = async () => {
               try {
                const decryptedCapacity = getCapacityFromQuery();
                   console.log('Fetching clause numbers for Organization section...');
       
                   const clauseResponse = await getClauseNo({ 
            standard: '2022', 
            labType: 'Medical', 
            userType: decryptedCapacity, 
            appid: appId, 
            orgid: orgId 
          });
       
                   if (clauseResponse.data?.isstatus && clauseResponse.data?.clause) {
                       setClauses(clauseResponse.data.clause);
                       console.log('Loaded clause numbers:', clauseResponse.data.clause.length);
                   } else {
                       console.warn('No clause numbers found or API returned isstatus: false');
                       setClauses([]);
                   }
               } catch (error) {
                   console.error('Error fetching clause numbers:', error);
                   setClauses([]);
               }
           };

        loadClauseNumbers();
    }, [title, existingDANCRecord]);

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    // Helper function to filter clauses based on search
    const getFilteredClauses = React.useCallback(() => {
        if (!clauseSearchFilter) return clauses;
        const searchLower = clauseSearchFilter.toLowerCase();
        
        return clauses.filter(clause => {
            const clauseNoMatch = clause?.clauseNo?.toLowerCase().includes(searchLower);
            const nameMatch = clause?.name?.toLowerCase().includes(searchLower);
            const displayNameMatch = clause?.displayName?.toLowerCase().includes(searchLower);
            return clauseNoMatch || nameMatch || displayNameMatch;
        });
    }, [clauses, clauseSearchFilter]);

    // Handle clause checkbox change with auto-population of remarks
    const handleClauseCheckboxChange = React.useCallback((clause, isChecked) => {
        setSelectedClauses(prev => {
            let newSelection;
            if (isChecked) {
                // Add clause if not already present
                // Use id and name combination for unique identification since multiple clauses can have same clauseNo
                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                const exists = prev.some(c => {
                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                    return existingKey === cKey;
                });
                if (!exists) {
                    newSelection = [...prev, clause];
                } else {
                    newSelection = [...prev];
                }
            } else {
                // Remove clause if present
                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                newSelection = prev.filter(c => {
                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                    return existingKey !== cKey;
                });
            }
            
            // Update remarks with selected clause names
            const clauseNames = newSelection.map(c => c?.name || c?.displayName || c?.clauseNo || c).join(', ');
            handleAdequacyRemarks(sectionId, clauseNames);
            
            return newSelection;
        });
    }, [handleAdequacyRemarks, sectionId]);

    const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

    // Fetch scrutiny remarks when inadequate status is selected
    useEffect(() => {
        const fetchScrutinyRemarks = async () => {
            if (adequacy.status === 'Inadequate') {
                const type = localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review';
                try {
                    setLoadingRemarks(true);
                    // Get only Document Review remarks
                    const documentRemarks = await getScrutinyRemarks({ sectionName: title, type });
                    
                    // Set only Document Review remarks
                    if (documentRemarks.data.success) {
                        const remarks = (documentRemarks.data.data || []).map(r => ({...r, type}));
                        setScrutinyRemarks(remarks);
                    } else {
                        setScrutinyRemarks([]);
                    }
                } catch (error) {
                    console.error('Error fetching scrutiny remarks:', error);
                    setScrutinyRemarks([]);
                } finally {
                    setLoadingRemarks(false);
                }
            } else {
                setScrutinyRemarks([]);
            }
        };

        fetchScrutinyRemarks();
    }, [adequacy.status, title]);

    // Don't render if not in adequacy check stage or user type
    // Stage 18: Only show for userTypeId 4 and 6 (editable)
    // Stage 18b: Only show for userTypeId 4 and 6 (editable) - works same as stage 18
    // Stage 18a: Show for any user type (read-only)
    // Stage 20: Only show for userTypeId 4 and 6 (editable) - works same as stage 18
    const userTypeId = localStorage.getItem('userTypeId');
    if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && userTypeId !== '4' && userTypeId !== '6') return null;
    if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b" && applicationStage !== "20") return null;

    // Check if adequacy checks should be read-only (stage 18a)
    const isAdequacyReadOnly = applicationStage === "18a";

    // Standard status change handler (no DA NC calls)
    const handleStatusChange = (status) => {
        if (!isAdequacyReadOnly) {
            handleAdequacyCheck(sectionId, status);
        }
    };

    // Standard remarks change handler with dropdown tracking
    const handleRemarksChange = (remarks, fromDropdown = false) => {
        if (!isAdequacyReadOnly) {
            handleAdequacyRemarks(sectionId, remarks);
            setIsDropdownSelection(fromDropdown);
        }
    };

    // Enhanced submit handler with DA NC API call
    const handleSubmit = async () => {
        try {
            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            // First submit the adequacy check
            await handleAdequacySubmit(sectionId);

            // Then create/update DA NC record based on status
            if (adequacy.status) {
                if (existingDANCRecord) {
                    // Update existing record
                    try {
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default
                        
                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }
                        
                        console.log(`💾 Updating DANC record for userType ${userTypeId} with NCtype: ${ncType}`);

                        // Get assessment parameters from URL for Assessment users
                        const assessmentId = getAssessmentIdFromQuery();
                        const assessorId = getAssessorIdFromQuery();

                        const updateData = {
                            subsectionName: sectionId,
                            informationStatus: adequacy.status,
                            observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : '',
                            NCtype: ncType,
                            ClauseNo: userTypeId === '6' ? selectedClauses.map(c => c?.clauseNo || c?.displayName || c?.name || '').filter(Boolean).join(', ') : null,
                            isactive: 1
                        };

                        console.log('Updating Record with data:', updateData);
                        console.log('Assessment parameters - assessmentId:', assessmentId, 'assessorId:', assessorId);
                        
                        // Use updateDANCBySourceId to update specific record by sourceid
                        const recordId = existingDANCRecord.sourceid || existingDANCRecord.Id;
                        console.log(`💾 Updating DANC record with sourceid: ${recordId}`);
                        
                        const response = await updateDANCBySourceId(
                            orgId, 
                            appId, 
                            'Organization Details', 
                            recordId, 
                            assessmentId || 0, 
                            assessorId || 0, 
                            updateData
                        );
                        console.log('DA-NC update response:', response);

                        if (response.success) {
                            setExistingDANCRecord(response.data); // Update local state with new data
                            setDancSuccess('Record updated successfully');
                            
                            // Refresh DA-NC data to get updated records
                            if (typeof refreshDANCData === 'function') {
                                await refreshDANCData();
                            }
                        } else {
                            throw new Error(response.message || 'Update failed');
                        }
                    } catch (updateError) {
                        console.error('DA-NC update failed:', updateError);
                        setDancError('Failed to Update: ' + updateError.message);
                    }
                } else {
                    // Create new record  
                    try {
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default
                        
                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }
                        
                        console.log(`✨ Creating new DANC record for userType ${userTypeId} with NCtype: ${ncType}`);
                        
                        // Get assessment parameters from URL for Assessment users
                        const assessmentId = getAssessmentIdFromQuery();
                        const assessorId = getAssessorIdFromQuery();

                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: 'Organization Details',
                            SubsectionName: sectionId,
                            InformationProvided: adequacy.remarks || '',
                            InformationStatus: adequacy.status,
                            Observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : '',
                            Insertedby: currentUserId,
                            NCtype: ncType,
                            ClauseNo: userTypeId === '6' ? selectedClauses.map(c => c?.clauseNo || c?.displayName || c?.name || '').filter(Boolean).join(', ') : null,
                            assessmentId: userTypeId === '6' ? (assessmentId ? parseInt(assessmentId) : null) : 0,
                            assessorId: userTypeId === '6' ? (assessorId ? parseInt(assessorId) : null) : localStorage.getItem('orgId'),
                        };

                        console.log('Creating new Record with data:', dancData);
                        console.log('Assessment parameters - assessmentId:', assessmentId, 'assessorId:', assessorId);
                        
                        const response = await insertDANC(dancData);
                        console.log('DA-NC insert response:', response);

                        if (response.success) {
                            setExistingDANCRecord(response.data); // Store the created record
                            setDancSuccess('Record created successfully');
                            
                            // Refresh DA-NC data to get updated records
                            if (typeof refreshDANCData === 'function') {
                                await refreshDANCData();
                            }
                        } else {
                            throw new Error(response.message || 'Insert failed');
                        }
                    } catch (insertError) {
                        console.error('DA-NC creation failed:', insertError);
                        setDancError('Failed to create Record: ' + insertError.message);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" sx={{
                        color: '#495057',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                    }}>
                        📋 Adequacy Check - {title}
                    </Typography>
                    
                    {/* Assessment Status and Clause Chips Display */}
                    {adequacy.status && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                                label={`Assessment: ${adequacy.status}`}
                                size="small"
                                color={adequacy.status === 'Adequate' ? 'success' : adequacy.status === 'Inadequate' ? 'error' : 'info'}
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                }}
                            />
                            {/* Display selected clause numbers as chips */}
                            {selectedClauses.length > 0 && adequacy.status === 'Inadequate' && (
                                selectedClauses.map((clause, index) => (
                                    <Chip
                                        key={`clause-chip-${clause?.id || index}-${clause?.clauseNo || ''}`}
                                        label={`Clause: ${clause?.clauseNo || clause?.displayName || clause?.name || clause}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{
                                            fontSize: '0.7rem',
                                            height: '24px',
                                            '& .MuiChip-label': {
                                                px: 1
                                            }
                                        }}
                                    />
                                ))
                            )}
                        </Box>
                    )}
                </Box>

                {/* {existingDANCRecord && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={`Record: ${existingDANCRecord.InformationStatus || 'Active'}`}
                            size="small"
                            sx={{
                                bgcolor: existingDANCRecord.InformationStatus === 'Adequate' ? '#28a745' : '#dc3545',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                        {existingDANCRecord.ClauseNo && (
                            <Chip
                                label={`Clause: ${existingDANCRecord.ClauseNo}`}
                                size="small"
                                sx={{
                                    bgcolor: '#2196f3',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            />
                        )}
                    </Box>
                )} */}

                {dancLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Loading DA NC data...</Typography>
                    </Box>
                )}
            </Box>



            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                            disabled={isAdequacyReadOnly}
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                            {userTypeId === '4' && (
                            <MenuItem value="To be Verify">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VerifyIcon sx={{ color: '#35dcdcff', fontSize: '1rem' }} />
                                    To be Verify
                                </Box>
                            </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Clause Number Selection - Show for Assessment users (userType 6) */}
                {(localStorage.getItem('userTypeId') === '6' && adequacy.status === 'Inadequate') && (
                    <Grid item xs={12}>
                        <Box sx={{
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: "white",
                            "&:hover": {
                                boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                            },
                        }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                📋 Select Clause Numbers (Multiple Selection Allowed)
                            </Typography>
                            
                            {/* Quick action buttons */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        const filteredClauses = getFilteredClauses();
                                        setSelectedClauses(prev => {
                                            const newSelection = [...prev];
                                            filteredClauses.forEach(clause => {
                                                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                                                const exists = newSelection.some(c => {
                                                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                                                    return existingKey === cKey;
                                                });
                                                if (!exists) {
                                                    newSelection.push(clause);
                                                }
                                            });
                                            // Update remarks
                                            const clauseNames = newSelection.map(c => c?.name || c?.displayName || c?.clauseNo || c).join(', ');
                                            handleAdequacyRemarks(sectionId, clauseNames);
                                            return newSelection;
                                        });
                                    }}
                                    disabled={isAdequacyReadOnly}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Select All {clauseSearchFilter ? 'Visible' : ''}
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        setSelectedClauses([]);
                                        handleAdequacyRemarks(sectionId, '');
                                    }}
                                    disabled={selectedClauses.length === 0 || isAdequacyReadOnly}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                            
                            {/* Search bar for clauses */}
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search clause numbers or names..."
                                value={clauseSearchFilter}
                                onChange={(e) => setClauseSearchFilter(e.target.value)}
                                inputRef={clauseSearchInputRef}
                                disabled={isAdequacyReadOnly}
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : '#f8f9fa',
                                        '& input': {
                                            py: 1,
                                        }
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <Box sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box>
                                    ),
                                    endAdornment: clauseSearchFilter && !isAdequacyReadOnly && (
                                        <Box 
                                            onClick={() => {
                                                setClauseSearchFilter("");
                                                clauseSearchInputRef.current?.focus();
                                            }}
                                            sx={{ 
                                                cursor: 'pointer', 
                                                color: 'text.secondary',
                                                '&:hover': { color: 'error.main' }
                                            }}
                                        >
                                            ✕
                                        </Box>
                                    ),
                                }}
                            />
                            
                            <FormGroup row sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                {getFilteredClauses().map((clause, index) => (
                                    <FormControlLabel
                                        key={`clause-${clause?.id || index}-${clause?.clauseNo || ''}`}
                                        control={
                                            <Checkbox
                                                checked={selectedClauses.some(c => {
                                                    // Use id and name combination for unique identification
                                                    const cKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                                                    const clauseKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                                                    return cKey === clauseKey;
                                                })}
                                                onChange={(e) => handleClauseCheckboxChange(clause, e.target.checked)}
                                                color="primary"
                                                disabled={isAdequacyReadOnly}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {clause?.clauseNo || clause?.displayName || clause}
                                                </Typography>
                                                {clause?.name && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {clause.name}
                                                    </Typography>
                                                )}
                                                {clause?.displayName && clause?.clauseNo && clause?.displayName !== clause?.name && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {clause.displayName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ 
                                            m: 0.5, 
                                            minWidth: '200px',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                ))}
                            </FormGroup>
                            
                            {/* Search results info */}
                            {clauseSearchFilter && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                                    {getFilteredClauses().length} clause(s) found for "{clauseSearchFilter}"
                                </Typography>
                            )}
                            
                            {/* No results message */}
                            {clauseSearchFilter && getFilteredClauses().length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                                    <Typography variant="body2">
                                        🔍 No clauses found matching "{clauseSearchFilter}"
                                    </Typography>
                                    <Typography variant="caption">
                                        Try searching by clause number, name, or display name
                                    </Typography>
                                </Box>
                            )}
                            
                            {selectedClauses.length > 0 && (
                                <Typography variant="caption" sx={{ color: 'primary.main', mt: 1, display: 'block' }}>
                                    ✓ {selectedClauses.length} clause(s) selected
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                )}

                {adequacy.status === 'Inadequate' && (
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={
                                isAdequacyReadOnly 
                                    ? "Remarks (View Only)" 
                                    : "Remarks (Required)"
                            }
                            placeholder={isAdequacyReadOnly ? 
                                "View Only (Read-only)" : 
                                "Enter your remarks here..."
                            }
                            value={adequacy.remarks || ''}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleRemarksChange(e.target.value, true)}
                            required={!isAdequacyReadOnly}
                            variant="outlined"
                            InputProps={{
                                readOnly: isAdequacyReadOnly,
                                style: isAdequacyReadOnly ? { 
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed'
                                } : { backgroundColor: 'white' }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    cursor: isAdequacyReadOnly ? 'not-allowed' : 'text',
                                    '&:hover': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(220,53,69,0.15)',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(220,53,69,0.25)',
                                    }
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{ 
                            color: '#6c757d',
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                        }}>
                            {isAdequacyReadOnly 
                                ? "Adequacy check is in read-only mode for stage 18a."
                                : "Remarks will auto-populate based on selected clause numbers. You can edit as needed."
                            }
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    {/* <Alert
                        severity={existingDANCRecord
                            ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
                            : (adequacy.status === 'Adequate' ? 'success' : 'warning')
                        }
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.2rem'
                            }
                        }}
                    >
                        {existingDANCRecord
                            ? (adequacy.status === 'Adequate'
                                ? `✓ Record exists with status: ${existingDANCRecord.InformationStatus}. Click Update to modify.`
                                : `⚠ Record exists with status: ${existingDANCRecord.InformationStatus}. Click Update to modify.`)
                            : (adequacy.status === 'Adequate'
                                ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                                : '⚠ Please address the remarks above and submit to create Record.')}
                    </Alert> */}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && (!adequacy.remarks || adequacy.remarks.toString().trim() === '')) ||
                                ((localStorage.getItem('userTypeId') === '6' && adequacy.status === 'Inadequate') && selectedClauses.length === 0) ||
                                loading ||
                                dancLoading ||
                                isAdequacyReadOnly
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' // Blue gradient for update
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {existingDANCRecord ? (
                                        <>
                                            🔄 Update
                                        </>
                                    ) : (
                                        <>
                                            📤 submit
                                        </>
                                    )}
                                </Box>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const OrganizationSection = ({ data, onUpdate, fields = [] }) => {
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('📍 OrganizationSection - Decrypted orgId from URL query:', decryptedOrgId);
                console.log('📍 OrganizationSection - Original encrypted orgId:', encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('⚠️ OrganizationSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 OrganizationSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 OrganizationSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ OrganizationSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get orgId with priority: URL query (encrypted) > URL params > no default
    const getOrgId = useCallback(() => {
        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: URL query (encrypted) only
    const getAppId = useCallback(() => {
        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    // Function to get assessmentId from URL query parameters
  const getAssessmentIdFromQuery = () => {
         const searchParams = new URLSearchParams(location.search);
         const encryptedAssessmentId = searchParams.get("assessmentId");
         if (encryptedAssessmentId) {
             try {
                 const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                 console.log(
                     "📍 Using decrypted assessmentId from URL query:",
                     decryptedAssessmentId
                 );
                 return decryptedAssessmentId;
             } catch (error) {
                 return encryptedAssessmentId;
             }
         }
         return 0;
     };

     // Function to get assessorId from URL query parameters with decryption
       const getAssessorIdFromQuery = () => {
           const searchParams = new URLSearchParams(location.search);
           const encryptedAssessorId = searchParams.get("assessorId");
           if (encryptedAssessorId) {
               try {
                   const decryptedAssessorId = decryptText(encryptedAssessorId);
                   console.log(
                       "📍 Using decrypted assessorId from URL query:",
                       decryptedAssessorId
                   );
                   return decryptedAssessorId;
               } catch (error) {
                   return encryptedAssessorId;
               }
           }
           return 0;
       };
   

    const [orgId, setOrgId] = useState(() => {
        // Initialize with query parameters first, then URL params
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('🚀 Initial orgId from encrypted query:', decryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
                return encryptedOrgId;
            }
        }
        // Only fallback to URL params, no localStorage
        return urlOrgId || null;
    });
    
    const [appId, setAppId] = useState(() => {
        // Initialize with query parameters only
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('🚀 Initial appId from encrypted query:', decryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
                return encryptedAppId;
            }
        }
        // No fallback - must be provided via query parameters
        return null;
    });
    const [applicationStage, setApplicationStage] = useState(null); // Application stage from LabApplications table

    // Update orgId state only (no localStorage)
    const updateOrgId = (newOrgId) => {
        console.log('Updating orgId state:', newOrgId);
        setOrgId(newOrgId);
    };

    // Update appId state only (no localStorage)
    const updateAppId = (newAppId) => {
        console.log('Updating appId state:', newAppId);
        setAppId(newAppId);
    };
    // Get application stage from LabApplications table
    const getApplicationStage = useCallback(async () => {
        try {
            if (!appId) return;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
                console.log('Fetched application stage:', result.stage);
            } else {
                console.log('Stage not found, defaulting to stage 1 for demo purposes');
                setApplicationStage(1); // Default to 1 for demo purposes
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            console.log('Error occurred, defaulting to stage 1 for demo purposes');
            setApplicationStage(1); // Default to 1 for demo purposes
        }
    }, [appId]);

    useEffect(() => {
        getApplicationStage();
        const currentOrgId = getOrgId();
        if (currentOrgId !== orgId) {
            updateOrgId(currentOrgId);
        }

        const currentAppId = getAppId();
        if (currentAppId !== appId) {
            console.log('AppId from URL/localStorage:', currentAppId);
            updateAppId(currentAppId);
        }
    }, [urlOrgId, location.search, orgId, appId, getApplicationStage, getOrgId, getAppId]);

    const [files, setFiles] = useState({
        managementSystemDocument: data?.managementSystemDocument || null,
        organizationChart: data?.organizationChart || null,
        parentOrganizationChart: data?.parentOrganizationChart || null,
    });


    const [isPartOfOrganization, setIsPartOfOrganization] = useState(false);
    const [loadingLaboratoryDetails, setLoadingLaboratoryDetails] = useState(true);



    // New Adequacy Check states for DA-NC integration
    const [adequacyChecks, setAdequacyChecks] = useState({});

    // Unfreeze records state
    const [unfreezeRecords, setUnfreezeRecords] = useState([]);

    // Assessment tracking hook for location and hourly attendance
    const {
        locationWarningOpen,
        distanceMessage,
        attendanceCheckDialogOpen,
        attendanceCheckMessage,
        setLocationWarningOpen,
        setAttendanceCheckDialogOpen,
        checkHourlyAttendance,
    } = useAssessmentTracking({
        assessorId: getAssessorIdFromQuery(),
        appId,
        orgId,
    });

    // Current user for DA NC system - get from auth context or session
    const [currentUser] = useState(() => {
        return localStorage.getItem('userName') ||
            localStorage.getItem('currentUser') ||
            'System User';
    });

    // Current user ID for DA NC system - get numeric ID for Insertedby field
    const [currentUserId] = useState(() => {
        return parseInt(localStorage.getItem('userId')) ||
            parseInt(localStorage.getItem('currentUserId')) ||
            0;
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Helper function to check if a section is unfrozen based on API data
    const isSectionUnfrozen = (sectionTitle) => {
        if (!unfreezeRecords || unfreezeRecords.length === 0) return false;

        const today = new Date();
        // Reset time to start of day for date-only comparison
        today.setHours(0, 0, 0, 0);

        return unfreezeRecords.some(record => {
            if (record.sectionname !== sectionTitle) return false;

            const expiryDate = new Date(record.expirydate);
            // Reset expiry date time to start of day for date-only comparison
            expiryDate.setHours(0, 0, 0, 0);

            // Return true if expiry date is today or in the future (still valid for editing)
            // Compare dates only, not timestamps
            return expiryDate.getTime() >= today.getTime();
        });
    };

    // Helper function to check if field should be read-only based on application stage and user type
    const isFieldReadOnly = () => {
        const userTypeId = localStorage.getItem('userTypeId');

        // Check if organization section is unfrozen first - this overrides ALL other conditions
        if (isSectionUnfrozen('Organization Details')) {
            console.log('Organization Details section is unfrozen, allowing edits');
            return false; // Allow editing if section is unfrozen
        }

        // Stage 18 or 18b with userTypeId 4 and 6: all fields are read-only except adequacy checks
        if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (userTypeId === '4' || userTypeId === '6')) {
            return true;
        }

        // Stage 18a with any userTypeId: only inadequate sections are editable, adequacy checks are read-only
        if (applicationStage === "18a") {
            const adequacy = adequacyChecks['organization-details'];
            // If section is marked as Inadequate, it's editable; otherwise read-only
            return adequacy?.status !== 'Inadequate';
        }

        return false; // Default: not read-only
    };

    // Handle adequacy check change with useCallback to prevent re-renders
    const handleAdequacyCheck = useCallback((sectionId, value) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                status: value,
                lastUpdated: new Date().toISOString()
            }
        }));
    }, []);

    // Handle adequacy remarks change with useCallback to prevent re-renders
    const handleAdequacyRemarks = useCallback((sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                remarks: remarks,
                lastUpdated: new Date().toISOString()
            }
        }));
    }, []);

    // Handle adequacy check submission
    const handleAdequacySubmit = async (sectionId) => {
        const adequacy = adequacyChecks[sectionId];
        if (!adequacy || !adequacy.status) {
            console.warn('No adequacy status to submit for section:', sectionId);
            return;
        }

        try {
            console.log(`Submitting adequacy check for ${sectionId}:`, adequacy);
            // Mark as submitted locally
            setAdequacyChecks(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    isSubmitted: true,
                    submittedAt: new Date().toISOString()
                }
            }));

            showSnackbar(`Adequacy check for ${sectionId} submitted successfully!`, 'success');
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            showSnackbar('Failed to submit adequacy check', 'error');

            // Revert submission status on error
            setAdequacyChecks(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    isSubmitted: false,
                    submittedAt: null
                }
            }));
        }
    };

    // Load DA NC data for all sections
    const loadDANCData = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // Determine expected NCtype based on userTypeId
            const userTypeId = localStorage.getItem('userTypeId');
            let expectedNCType = 'Document Adequacy'; // Default
            
            if (userTypeId === '4') {
                expectedNCType = 'Document Adequacy';
            } else if (userTypeId === '6') {
                expectedNCType = 'Assessment';
            }
            
            console.log(`� OrganizationSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);
            
            // Get all DA NC records for this org and app
            const response = await getDANC({
                orgid: orgId,
                appid: appId,
                isactive: 1,
                limit: 600  // Increase limit to get all records
            });

            if (response.data.success && response.data.data && response.data.data.length > 0) {
                console.log(`📋 Found ${response.data.data.length} total records, filtering by NCtype: ${expectedNCType}`);
                
                // Filter records by NCtype before processing
                const filteredRecords = response.data.data.filter(record => 
                    record.NCtype === expectedNCType
                );
                
                console.log(`🎯 Filtered to ${filteredRecords.length} records with NCtype: ${expectedNCType}`);
                
                // Create adequacy checks from filtered DA NC data
                const dancAdequacyChecks = {};

                filteredRecords.forEach(record => {

                    // Handle both SubsectionName and direct mapping from SectionName
                    let sectionKey = record.SubsectionName;

                    // If SubsectionName is not available, try to map from SectionName
                    if (!sectionKey && record.SectionName) {
                        const sectionMappings = {
                            'Organization Details': 'organization-details',
                            'Organization': 'organization-details',
                            'Management System': 'management-system',
                            'Organization Chart': 'organization-chart'
                        };
                        sectionKey = sectionMappings[record.SectionName] || record.SectionName.toLowerCase().replace(/\s+/g, '-');
                    }

                    if (sectionKey) {
                        dancAdequacyChecks[sectionKey] = {
                            status: record.InformationStatus || '',
                            remarks: record.InformationProvided || record.Observation || '',
                            isSubmitted: false, // Allow users to modify and resubmit
                            submittedAt: record.updatedate || record.Insertdate,
                            dancRecord: record, // Store original record for reference
                            sourceid: record.sourceid || record.Id // Store sourceid for updates
                        };
                        
                        console.log(`✅ Stored DANC record for section "${sectionKey}" with sourceid: ${record.sourceid || record.Id}`);

                        console.log(`Mapped Record to section "${sectionKey}":`, {
                            status: record.InformationStatus,
                            remarks: record.InformationProvided || record.Observation
                        });
                    }
                });

                // Merge with existing adequacy checks
                setAdequacyChecks(prev => {
                    const merged = {
                        ...prev,
                        ...dancAdequacyChecks
                    };
                    console.log('Final merged adequacy checks:', merged);
                    return merged;
                });

            } else {
                console.log('No Records found in response');
            }
        } catch (error) {
            console.error('Error loading DA-NC data:', error);
            showSnackbar('Error loading DA-NC data: ' + error.message, 'error');
        }
    }, [orgId, appId]);

    // Load unfreeze records
    const loadUnfreezeRecords = useCallback(async () => {
        if (!appId) return;

        try {
            const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
            if (response.success) {
                console.log('Unfreeze records loaded:', response.data);
                setUnfreezeRecords(response.data);
            }
        } catch (err) {
            console.error("Load unfreeze records error:", err);
        }
    }, [appId, orgId]);

    // Load laboratory details to check if laboratory is part of organization  
    const loadLaboratoryDetails = useCallback(async () => {
        try {
            setLoadingLaboratoryDetails(true);
            console.log('🔄 OrganizationSection - Loading laboratory details with orgId:', orgId);
            const response = await LaboratoryService.getLaboratoryDetails(orgId);

            if (response.success && response.data) {
                // Check if laboratory is part of organization (IsParentOrgApplicable field)
                const isPartOfOrg = response.data.IsParentOrgApplicable === true ||
                    response.data.IsParentOrgApplicable === 'Yes' ||
                    response.data.IsParentOrgApplicable === 1;
                setIsPartOfOrganization(isPartOfOrg);
                console.log('Laboratory is part of organization:', isPartOfOrg);
            } else {
                setIsPartOfOrganization(false);
                console.log('No laboratory details found or failed to load');
            }
        } catch (error) {
            console.error('Error loading laboratory details:', error);
            setIsPartOfOrganization(false);
        } finally {
            setLoadingLaboratoryDetails(false);
        }
    }, [orgId]);


    // Load existing organization documents on component mount
    const loadOrganizationDocuments = useCallback(async () => {
        try {
            console.log('📄 OrganizationSection - Loading organization documents with appId:', appId);
            const response = await LaboratoryService.getOrganizationDocuments(appId);

            if (response.success && response.data) {
                const existingFiles = {};

                // Use batch processing utility for cleaner code
                const fileFields = ['managementSystemDocument', 'organizationChart', 'parentOrganizationChart'];
                const processedFiles = await s3Utils.batchProcessForm153Files(response.data, fileFields);

                // Map the processed files to the expected structure
                Object.assign(existingFiles, processedFiles);

                setFiles(prevFiles => ({ ...prevFiles, ...existingFiles }));


            }
        } catch (error) {
            console.error('Error loading organization documents:', error);
            // Don't show error snackbar for missing documents as it's normal for new records
        }
    }, [appId]);

    // Load initial data including DA-NC data
    const loadInitialData = useCallback(async () => {
        if (orgId && appId) { // Only load if we have both orgId and appId from URL parameters
            await loadLaboratoryDetails();
            await loadOrganizationDocuments();
            await loadDANCData();
            await loadUnfreezeRecords();
        } else {
            console.log('⚠️ Missing orgId or appId from URL parameters, skipping data load');
            setLoadingLaboratoryDetails(false);
        }
    }, [orgId, appId, loadLaboratoryDetails, loadOrganizationDocuments, loadDANCData, loadUnfreezeRecords]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);



    // Check if any files exist to determine button text
    const hasExistingFiles = () => {
        return Object.values(files).some(file => file !== null);
    };

    const handleFileChange = (fieldName, fileData) => {
        console.log(`File change for ${fieldName}:`, fileData);

        const newFiles = {
            ...files,
            [fieldName]: fileData,
        };
        setFiles(newFiles);
        onUpdate && onUpdate(newFiles);

        if (fileData) {
            showSnackbar('File uploaded successfully!');
        } else {
            showSnackbar('File removed successfully!', 'success');
        }
    };

    const handleSubmit = async () => {
        // Check required files based on laboratory configuration
        const requiredFiles = ['managementSystemDocument', 'organizationChart'];
        if (isPartOfOrganization) {
            requiredFiles.push('parentOrganizationChart');
        }

        const missingFiles = requiredFiles.filter(fileField => !files[fileField]);

        if (missingFiles.length > 0) {
            const fileLabels = {
                'managementSystemDocument': 'Management System Document',
                'organizationChart': 'Organization Chart',
                'parentOrganizationChart': 'Parent Organization Chart'
            };

            const missingFileNames = missingFiles.map(field => fileLabels[field]).join(', ');
            showSnackbar(`Please upload the following required document(s): ${missingFileNames}`, 'warning');
            return;
        }

        try {
            // Prepare document data with file names/keys
            // Try multiple possible properties to extract the filename
            const getFileName = (fileData) => {
                if (!fileData) return null;

                console.log('File data structure:', fileData);

                // The backend returns an object with properties like: key, fileName, originalName, etc.
                // Priority: use key first (the S3 key), then fileName, then originalName
                return fileData.key ||
                    fileData.fileName ||
                    fileData.originalName ||
                    fileData.name ||
                    fileData.file?.name ||
                    (typeof fileData === 'string' ? fileData : null);
            };

            const documentData = {
                managementSystemDocument: getFileName(files.managementSystemDocument),
                organizationChart: getFileName(files.organizationChart),
                parentOrganizationChart: getFileName(files.parentOrganizationChart)
            };

            console.log('Document data to send:', documentData);

            console.log('💾 OrganizationSection - Updating organization documents with appId:', appId, 'documentData:', documentData);
            
            // Call API to update organization documents
            const response = await LaboratoryService.updateOrganizationDocuments(appId, documentData);

            const isUpdate = hasExistingFiles();

            if (response.success) {
                showSnackbar(
                    isUpdate
                        ? 'Organization documents updated successfully!'
                        : 'Organization documents submitted successfully!',
                    'success'
                );
                console.log('API Response:', response);
            } else {
                showSnackbar(
                    response.message ||
                    (isUpdate
                        ? 'Failed to update organization documents'
                        : 'Failed to submit organization documents'),
                    'error'
                );
            }
        } catch (error) {
            console.error('Error submitting organization documents:', error);
            const isUpdate = hasExistingFiles();
            showSnackbar(
                error.message ||
                (isUpdate
                    ? 'Failed to update organization documents'
                    : 'Failed to submit organization documents'),
                'error'
            );
        }
    };

    return (
        <Box sx={{
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            maxWidth: '100vw',
            minHeight: '70vh',
        }}>
            {/* Header */}
            <Box sx={{
                py: 2,
                px: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                },
                '& .MuiSvgIcon-root': {
                    color: 'white',
                }
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    🏢 Organization Details
                </Typography>

                {/* Section Status Indicators */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* Unfrozen section indicator */}
                    {isSectionUnfrozen('Organization Details') && (
                        <Chip
                            label="🔓 UNFROZEN"
                            size="small"
                            sx={{
                                bgcolor: '#ff9800',
                                color: 'white',
                                fontWeight: 'bold',
                                animation: 'pulse 2s infinite'
                            }}
                        />
                    )}

                    {/* Stage 18 or 18b or 20 Read-only indicator */}
                    {(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4' || localStorage.getItem('userTypeId') === '6') && (
                        <Chip
                            label="📖 Read Only Mode"
                            size="small"
                            sx={{
                                bgcolor: '#2196f3',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}

                    {/* Stage 18a indicators */}
                    {applicationStage === "18a" && (
                        <>
                            {adequacyChecks['organization-details']?.status && (
                                <Chip
                                    label={`Organization Details: ${adequacyChecks['organization-details'].status}`}
                                    size="small"
                                    sx={{
                                        bgcolor: adequacyChecks['organization-details'].status === 'Adequate' ? '#4caf50' : '#f44336',
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }}
                                />
                            )}
                            <Chip
                                label="🔒 Adequacy Checks Read-Only"
                                size="small"
                                sx={{
                                    bgcolor: '#9e9e9e',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            />
                        </>
                    )}
                </Box>

            </Box>

            {/* Unfrozen Sections Notification */}
            {isSectionUnfrozen('Organization Details') && (
                <Alert severity="warning" sx={{ m: 2, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        🔓 Sections Unfrozen for Editing
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        The Organization Details section has been temporarily unfrozen for editing. You can now modify the fields and documents.
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="Organization Details"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Box>
                </Alert>
            )}

            {/* File Upload Grid */}
            {loadingLaboratoryDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading laboratory details...
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3} sx={{ p: 2 }}>

                    {/* DA-NC Integrated Adequacy Check Section */}
                    {(((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4' || localStorage.getItem('userTypeId') === '6')) || applicationStage === "18a") && (
                        <Grid item xs={12}>
                            <AdequacyCheckSection
                                sectionId="organization-details"
                                title="Organization Details"
                                applicationStage={applicationStage}
                                orgId={orgId}
                                appId={appId}
                                adequacyChecks={adequacyChecks}
                                handleAdequacyCheck={handleAdequacyCheck}
                                handleAdequacyRemarks={handleAdequacyRemarks}
                                handleAdequacySubmit={handleAdequacySubmit}
                                loading={false}
                                currentUser={currentUser}
                                currentUserId={currentUserId}
                                getAssessmentIdFromQuery={getAssessmentIdFromQuery}
                                getAssessorIdFromQuery={getAssessorIdFromQuery}
                                refreshDANCData={loadDANCData}
                            />
                        </Grid>
                    )}
                    {/* Management System Document */}
                    <Grid item xs={12} md={4}>
                        <Card
                            elevation={2}
                            sx={{
                                height: '100%',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 3,
                                },
                            }}
                        >
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                        Management System Document
                                        <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>
                                    </Typography>
                                    {isFieldReadOnly() && (
                                        <Chip
                                            label={(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? "📖 Read Only" : "🔒 Locked"}
                                            size="small"
                                            sx={{
                                                bgcolor: (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? '#2196f3' : '#9e9e9e',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                    {applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate' && (
                                        <Chip
                                            label="✏️ Editable"
                                            size="small"
                                            sx={{
                                                bgcolor: '#4caf50',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <FileUploadComponent
                                        fieldName="managementSystemDocument"
                                        label="Management System Document"
                                        orgId={orgId}
                                        section="organization"
                                        required={true}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        maxSize={250}
                                        multiple={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={files.managementSystemDocument}
                                        disabled={isFieldReadOnly()}
                                        readOnly={isFieldReadOnly()}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>



                    {/* Organization Chart */}
                    <Grid item xs={12} md={4}>
                        <Card
                            elevation={2}
                            sx={{
                                height: '100%',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 3,
                                },
                            }}
                        >
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                        Organization Chart
                                        <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>
                                    </Typography>
                                    {isFieldReadOnly() && (
                                        <Chip
                                            label={(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? "📖 Read Only" : "🔒 Locked"}
                                            size="small"
                                            sx={{
                                                bgcolor: (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? '#2196f3' : '#9e9e9e',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                    {applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate' && (
                                        <Chip
                                            label="✏️ Editable"
                                            size="small"
                                            sx={{
                                                bgcolor: '#4caf50',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <FileUploadComponent
                                        fieldName="organizationChart"
                                        label="Organization Chart"
                                        orgId={orgId}
                                        section="organization"
                                        required={true}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        maxSize={10}
                                        multiple={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={files.organizationChart}
                                        disabled={isFieldReadOnly()}
                                        readOnly={isFieldReadOnly()}
                                    />

                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Parent Organization Chart - Only show if laboratory is part of organization */}
                    {isPartOfOrganization && (
                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3,
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            Parent Organization Chart
                                            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>
                                        </Typography>
                                        {isFieldReadOnly() && (
                                            <Chip
                                                label={(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? "📖 Read Only" : "🔒 Locked"}
                                                size="small"
                                                sx={{
                                                    bgcolor: (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") ? '#2196f3' : '#9e9e9e',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )}
                                        {applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate' && (
                                            <Chip
                                                label="✏️ Editable"
                                                size="small"
                                                sx={{
                                                    bgcolor: '#4caf50',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <FileUploadComponent
                                            fieldName="parentOrganizationChart"
                                            label="Parent Organization Chart"
                                            orgId={orgId}
                                            section="organization"
                                            required={true}
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            maxSize={10}
                                            multiple={false}
                                            onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                            initialFile={files.parentOrganizationChart}
                                            disabled={isFieldReadOnly()}
                                            readOnly={isFieldReadOnly()}
                                        />

                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Information message when Parent Organization Chart is not applicable */}
                    {/* {!loadingLaboratoryDetails && !isPartOfOrganization && (
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Parent Organization Chart:</strong> Not applicable - This laboratory is not part of a larger organization.
                                    <br />
                                    <small>If this is incorrect, please ensure the "Is Laboratory part of an Organization" field is set to "Yes" in the Laboratory Section.</small>
                                </Typography>
                            </Alert>
                        </Grid>
                    )} */}



                    {/* Submit/Update Button */}
                    <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                        {/* {hasExistingFiles() && (
                            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                                <Typography variant="body2">
                                    📄 <strong>Documents Status:</strong> {Object.values(files).filter(file => file !== null).length} document(s) uploaded.
                                    Click "Update" to modify existing documents.
                                </Typography>
                            </Alert>
                        )} */}
                        {((applicationStage === null || applicationStage === "" || applicationStage === undefined)) && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                sx={{
                                    ml: 'auto',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: hasExistingFiles()
                                        ? 'linear-gradient(135deg, #ff9800, #f57c00)'
                                        : 'linear-gradient(135deg, #667eea, #764ba2)',
                                    '&:hover': {
                                        background: hasExistingFiles()
                                            ? 'linear-gradient(135deg, #f57c00, #ef6c00)'
                                            : 'linear-gradient(135deg, #5a6fd8, #6a42a0)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: hasExistingFiles()
                                            ? '0 8px 25px rgba(255, 152, 0, 0.4)'
                                            : '0 8px 25px rgba(102, 126, 234, 0.4)',
                                    },
                                }}
                            >
                                {hasExistingFiles() ? 'Update Organization Documents' : 'Submit Organization Documents'}
                            </Button>
                        )}

                        {/* Show update button for stage 18a only if organization-details section is inadequate OR if section is unfrozen */}
                        {(
    (applicationStage === "18a" && adequacyChecks['organization-details']?.status === 'Inadequate') ||    isSectionUnfrozen('Organization Details')) && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                sx={{
                                    ml: 'auto',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #ff9800, #f57c00)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #f57c00, #ef6c00)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(255, 152, 0, 0.4)',
                                    },
                                }}
                            >
                                Update Organization Documents
                            </Button>
                        )}
                    </Grid>
                </Grid>
            )}


            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Location Warning Dialog */}
            <LocationWarningDialog
                open={locationWarningOpen}
                onClose={() => setLocationWarningOpen(false)}
                distanceMessage={distanceMessage}
            />

            {/* Hourly Attendance Dialog */}
            <HourlyAttendanceDialog
                open={attendanceCheckDialogOpen}
                onClose={() => setAttendanceCheckDialogOpen(false)}
                message={attendanceCheckMessage}
                onRecheck={checkHourlyAttendance}
            />
        </Box>
    );
};

export default OrganizationSection;
